import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, Send } from 'lucide-react'

import { useSendMessage, useTemplates } from '@/features/messaging/hooks'
import { TEMPLATE_VARIABLES, type TemplateCategory } from '@/types/messaging'

interface ComposeMessageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Exactly one context id (resolved to a customer server-side). */
  customerId?: string
  dealId?: string
  workOrderId?: string
}

// SMS segments are 160 chars (GSM-7); show the count so staff notice split texts.
const SMS_SEGMENT = 160

export function ComposeMessageDialog({
  open,
  onOpenChange,
  customerId,
  dealId,
  workOrderId,
}: ComposeMessageDialogProps) {
  const { data: templates } = useTemplates()
  const sendMutation = useSendMessage()
  const [templateId, setTemplateId] = useState<string>('')
  const [body, setBody] = useState('')
  const [error, setError] = useState<string | null>(null)

  // In deal/work-order context only relevant categories make sense.
  const relevantCategories = new Set<TemplateCategory>(['general'])
  if (dealId) relevantCategories.add('customers')
  if (workOrderId) relevantCategories.add('service')

  const relevantTemplates = (templates ?? []).filter((t) => relevantCategories.has(t.category))

  const handleTemplateChange = (value: string) => {
    setTemplateId(value)
    const template = relevantTemplates.find((t) => t.id === value)
    if (template) setBody(template.body)
  }

  // Reset the draft each time the dialog opens. "Adjust state during render"
  // (React docs) avoids a setState-in-effect cascading render.
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (open) {
      setTemplateId('')
      setBody('')
      setError(null)
    }
  }

  const handleSend = () => {
    setError(null)
    sendMutation.mutate(
      {
        customer_id: customerId,
        deal_id: dealId,
        work_order_id: workOrderId,
        template_id: templateId || undefined,
        body: body || undefined,
      },
      {
        onSuccess: () => onOpenChange(false),
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to send message'),
      },
    )
  }

  const segments = Math.max(1, Math.ceil(body.length / SMS_SEGMENT))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Text Customer</DialogTitle>
          <DialogDescription>
            Send an SMS{dealId ? ' about this deal' : workOrderId ? ' about this work order' : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="message-template">Template</Label>
            <Select value={templateId} onValueChange={handleTemplateChange}>
              <SelectTrigger id="message-template">
                <SelectValue placeholder="Start from scratch or pick a template" />
              </SelectTrigger>
              <SelectContent>
                {relevantTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="message-body">Message</Label>
            <Textarea
              id="message-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your message…"
              className="min-h-28"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Variables: {TEMPLATE_VARIABLES.map((v) => `{{${v}}}`).join(' ')}</span>
              <span className={body.length > SMS_SEGMENT ? 'text-amber-600' : undefined}>
                {body.length} chars · {segments} SMS
              </span>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={sendMutation.isPending || body.trim().length === 0}
          >
            {sendMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
